import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Select, Button, Modal, Input, message, Radio, Card, Col, Row, Divider } from 'antd';
import { 
  InboxOutlined, 
  OrderedListOutlined, 
  PlayCircleOutlined, 
  CheckCircleOutlined, 
  RobotOutlined
} from '@ant-design/icons';
import { supportTicketsAPI } from '../api/supportTickets';

const CATEGORIES = {
  feature: { label: 'معجون یا طلسم جدید (ویژگی جدید)', emoji: '🧪', color: 'purple' },
  bug: { label: 'کرم فلوبر در دیگ (گزارش باگ)', emoji: '🐛', color: 'red' },
  financial: { label: 'حساب گالئون‌ها در گرینگوتز (مشکل مالی)', emoji: '🪙', color: 'gold' },
  technical: { label: 'شکستگی چوب‌دستی (مشکل فنی)', emoji: '🪄', color: 'cyan' },
  question: { label: 'سوال از هرماینی (سوال)', emoji: '🦉', color: 'blue' },
  other: { label: 'رازهای مکتوم (موارد دیگر)', emoji: '📜', color: 'default' }
};

const STATUS_LABELS = {
  pending: 'صندوق ورودی',
  todo: 'لیست کارها',
  in_progress: 'در حال انجام',
  resolved: 'حل شده',
  closed: 'بسته شده'
};

const STATUS_COLORS = {
  pending: 'orange',
  todo: 'blue',
  in_progress: 'cyan',
  resolved: 'green',
  closed: 'gray'
};

export default function SupportTicketManager() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewType, setViewType] = useState('board'); // 'board' or 'table'
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [statusVal, setStatusVal] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Table filters
  const [catFilter, setCatFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supportTicketsAPI.getAdminTickets();
      if (res.data?.success) {
        setTickets(res.data.tickets || []);
      }
    } catch (err) {
      message.error('خطا در بارگذاری پیام‌ها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const openTicketDetails = (ticket) => {
    setSelectedTicket(ticket);
    setAdminNotes(ticket.adminNotes || '');
    setStatusVal(ticket.status);
    setModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedTicket) return;
    setSubmitting(true);
    try {
      const res = await supportTicketsAPI.updateTicketStatus(selectedTicket._id, {
        status: statusVal,
        adminNotes
      });
      if (res.data?.success) {
        message.success('پیام با موفقیت بروزرسانی شد.');
        setModalOpen(false);
        loadTickets();
      }
    } catch (err) {
      message.error(err.response?.data?.message || 'خطا در ثبت تغییرات');
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickMove = async (ticketId, nextStatus) => {
    try {
      const res = await supportTicketsAPI.updateTicketStatus(ticketId, { status: nextStatus });
      if (res.data?.success) {
        message.success(`وضعیت پیام تغییر کرد.`);
        loadTickets();
      }
    } catch (err) {
      message.error('خطا در تغییر وضعیت سریع');
    }
  };

  // Grouping tickets for the Kanban Board
  const columns = {
    pending: tickets.filter(t => t.status === 'pending'),
    todo: tickets.filter(t => t.status === 'todo'),
    in_progress: tickets.filter(t => t.status === 'in_progress'),
    resolved: tickets.filter(t => t.status === 'resolved' || t.status === 'closed')
  };

  const tableColumns = [
    {
      title: 'موضوع',
      dataIndex: 'title',
      key: 'title',
      render: (title, record) => (
        <div style={{ cursor: 'pointer' }} onClick={() => openTicketDetails(record)}>
          <strong>{title}</strong>
          {record.aiAnswered && (
            <Tag color="purple" style={{ marginRight: 6 }} icon={<RobotOutlined />}>
              هوش مصنوعی
            </Tag>
          )}
        </div>
      )
    },
    {
      title: 'فرستنده',
      key: 'sender',
      render: (_, record) => {
        const u = record.user || {};
        const displayName = u.farsiFirstName && u.farsiLastName 
          ? `${u.farsiFirstName} ${u.farsiLastName}` 
          : `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username || 'نامشخص';
        return (
          <div>
            <div>{displayName}</div>
            <div style={{ fontSize: '11px', color: '#999' }}>{u.phoneNumber}</div>
          </div>
        );
      }
    },
    {
      title: 'نوع پیام',
      dataIndex: 'category',
      key: 'category',
      render: c => {
        const cat = CATEGORIES[c] || { label: c, emoji: '🦉' };
        return <span>{cat.emoji} {cat.label}</span>;
      }
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      render: s => <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s] || s}</Tag>
    },
    {
      title: 'تاریخ ارسال',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: d => new Date(d).toLocaleDateString('fa-IR')
    },
    {
      title: 'عملیات',
      key: 'action',
      render: (_, record) => (
        <Button size="small" onClick={() => openTicketDetails(record)}>بررسی</Button>
      )
    }
  ];

  const filteredTickets = tickets.filter(t => {
    if (catFilter && t.category !== catFilter) return false;
    if (statusFilter && t.status !== statusFilter) return false;
    return true;
  });

  return (
    <div style={{ padding: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <Radio.Group value={viewType} onChange={e => setViewType(e.target.value)}>
          <Radio.Button value="board">📋 برد مدیریت کارها (Kanban)</Radio.Button>
          <Radio.Button value="table">🔍 لیست کل نامه‌ها (Table)</Radio.Button>
        </Radio.Group>
        
        <Button onClick={loadTickets} loading={loading}>🔄 بروزرسانی</Button>
      </div>

      {viewType === 'board' ? (
        <Row gutter={16}>
          {/* Incoming Column */}
          <Col xs={24} md={6}>
            <div style={{ background: '#f5f5f5', borderRadius: 8, padding: 12, minHeight: 500 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <InboxOutlined style={{ color: '#ff9800', fontSize: 18 }} />
                <h4 style={{ margin: 0, fontWeight: 600 }}>صندوق ورودی ({columns.pending.length})</h4>
              </div>
              <Divider style={{ margin: '8px 0 16px 0' }} />
              {columns.pending.map(t => (
                <BoardCard key={t._id} ticket={t} onOpen={openTicketDetails}>
                  <Button 
                    type="primary" 
                    size="small" 
                    icon={<OrderedListOutlined />}
                    onClick={(e) => { e.stopPropagation(); handleQuickMove(t._id, 'todo'); }}
                    style={{ width: '100%', marginTop: 8 }}
                  >
                    انتقال به لیست کارها
                  </Button>
                </BoardCard>
              ))}
              {columns.pending.length === 0 && <EmptyColumn />}
            </div>
          </Col>

          {/* To Do Column */}
          <Col xs={24} md={6}>
            <div style={{ background: '#f5f5f5', borderRadius: 8, padding: 12, minHeight: 500 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <OrderedListOutlined style={{ color: '#1890ff', fontSize: 18 }} />
                <h4 style={{ margin: 0, fontWeight: 600 }}>لیست کارها ({columns.todo.length})</h4>
              </div>
              <Divider style={{ margin: '8px 0 16px 0' }} />
              {columns.todo.map(t => (
                <BoardCard key={t._id} ticket={t} onOpen={openTicketDetails}>
                  <Button 
                    type="primary" 
                    size="small" 
                    icon={<PlayCircleOutlined />}
                    onClick={(e) => { e.stopPropagation(); handleQuickMove(t._id, 'in_progress'); }}
                    style={{ width: '100%', marginTop: 8 }}
                  >
                    شروع کار
                  </Button>
                </BoardCard>
              ))}
              {columns.todo.length === 0 && <EmptyColumn />}
            </div>
          </Col>

          {/* In Progress Column */}
          <Col xs={24} md={6}>
            <div style={{ background: '#f5f5f5', borderRadius: 8, padding: 12, minHeight: 500 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <PlayCircleOutlined style={{ color: '#13c2c2', fontSize: 18 }} />
                <h4 style={{ margin: 0, fontWeight: 600 }}>در حال انجام ({columns.in_progress.length})</h4>
              </div>
              <Divider style={{ margin: '8px 0 16px 0' }} />
              {columns.in_progress.map(t => (
                <BoardCard key={t._id} ticket={t} onOpen={openTicketDetails}>
                  <Button 
                    type="primary" 
                    size="small" 
                    icon={<CheckCircleOutlined />}
                    onClick={(e) => { e.stopPropagation(); openTicketDetails(t); setStatusVal('resolved'); }}
                    style={{ width: '100%', marginTop: 8, background: '#52c41a', borderColor: '#52c41a' }}
                  >
                    نوشتن پاسخ و اتمام کار
                  </Button>
                </BoardCard>
              ))}
              {columns.in_progress.length === 0 && <EmptyColumn />}
            </div>
          </Col>

          {/* Resolved/Closed Column */}
          <Col xs={24} md={6}>
            <div style={{ background: '#f5f5f5', borderRadius: 8, padding: 12, minHeight: 500 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                <h4 style={{ margin: 0, fontWeight: 600 }}>پایان‌یافته ({columns.resolved.length})</h4>
              </div>
              <Divider style={{ margin: '8px 0 16px 0' }} />
              {columns.resolved.map(t => (
                <BoardCard key={t._id} ticket={t} onOpen={openTicketDetails} showStatus />
              ))}
              {columns.resolved.length === 0 && <EmptyColumn />}
            </div>
          </Col>
        </Row>
      ) : (
        <div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            <Select 
              placeholder="فیلتر نوع تیکت"
              allowClear
              value={catFilter || undefined}
              onChange={setCatFilter}
              style={{ width: 200 }}
            >
              {Object.entries(CATEGORIES).map(([k, v]) => (
                <Select.Option key={k} value={k}>{v.emoji} {v.label}</Select.Option>
              ))}
            </Select>

            <Select 
              placeholder="فیلتر وضعیت"
              allowClear
              value={statusFilter || undefined}
              onChange={setStatusFilter}
              style={{ width: 180 }}
            >
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <Select.Option key={k} value={k}>{v}</Select.Option>
              ))}
            </Select>
          </div>
          <Table 
            columns={tableColumns} 
            dataSource={filteredTickets} 
            rowKey="_id"
            loading={loading}
          />
        </div>
      )}

      {/* Ticket Details and Update Action Modal */}
      {selectedTicket && (
        <Modal
          title={`📬 جزئیات نامه جادویی: ${selectedTicket.title}`}
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          onOk={handleUpdate}
          okText="ثبت تغییرات"
          cancelText="انصراف"
          confirmLoading={submitting}
          width={700}
        >
          <div style={{ marginTop: 15 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <strong>👤 فرستنده:</strong> {selectedTicket.user?.farsiFirstName && selectedTicket.user?.farsiLastName
                  ? `${selectedTicket.user?.farsiFirstName} ${selectedTicket.user?.farsiLastName}`
                  : `${selectedTicket.user?.firstName || ''} ${selectedTicket.user?.lastName || ''}`.trim() || selectedTicket.user?.username || 'نامشخص'}
              </Col>
              <Col xs={24} sm={12}>
                <strong>📞 شماره تماس:</strong> {selectedTicket.user?.phoneNumber || '—'}
              </Col>
              <Col xs={24} sm={12}>
                <strong>🏷️ نوع درخواست:</strong> {CATEGORIES[selectedTicket.category]?.emoji} {CATEGORIES[selectedTicket.category]?.label}
              </Col>
              <Col xs={24} sm={12}>
                <strong>📅 تاریخ ارسال:</strong> {new Date(selectedTicket.createdAt).toLocaleString('fa-IR')}
              </Col>
            </Row>

            <Divider style={{ margin: '16px 0' }} />

            <strong>📝 متن پیام جادوگر:</strong>
            <div style={{ background: '#f9f9f9', padding: '12px', borderRadius: '8px', border: '1px solid #eee', marginTop: 8, whiteSpace: 'pre-wrap' }}>
              {selectedTicket.description}
            </div>

            {selectedTicket.aiAnswered && (
              <>
                <Divider style={{ margin: '16px 0' }} />
                <div style={{ background: 'rgba(114, 47, 55, 0.05)', border: '1px dashed #722f37', padding: '12px', borderRadius: '8px' }}>
                  <strong style={{ color: '#722f37' }}><RobotOutlined /> پاسخ خودکار ارسالی توسط دامبلدور:</strong>
                  <p style={{ marginTop: 6, fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{selectedTicket.aiAnswer}</p>
                </div>
              </>
            )}

            {selectedTicket.aiSuggestedReply && (
              <>
                <Divider style={{ margin: '16px 0' }} />
                <div style={{ background: 'rgba(24, 144, 255, 0.05)', border: '1px dashed #1890ff', padding: '12px', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <strong style={{ color: '#1890ff' }}><RobotOutlined /> پیشنهاد هوش مصنوعی برای پاسخ:</strong>
                    <Button 
                      type="link" 
                      size="small" 
                      onClick={() => setAdminNotes(selectedTicket.aiSuggestedReply)}
                      style={{ padding: 0 }}
                    >
                      ✍️ استفاده از این پاسخ
                    </Button>
                  </div>
                  <p style={{ margin: 0, fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{selectedTicket.aiSuggestedReply}</p>
                </div>
              </>
            )}

            <Divider style={{ margin: '16px 0' }} />

            <div style={{ marginBottom: 15 }}>
              <strong style={{ display: 'block', marginBottom: 8 }}>🔮 تغییر وضعیت نامه:</strong>
              <Select 
                value={statusVal} 
                onChange={setStatusVal} 
                style={{ width: '100%' }}
              >
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <Select.Option key={k} value={k}>{v}</Select.Option>
                ))}
              </Select>
            </div>

            <div>
              <strong style={{ display: 'block', marginBottom: 8 }}>🏰 توضیحات و پاسخ دفتر مدیریت (این یادداشت برای کاربر قابل مشاهده است):</strong>
              <Input.TextArea 
                rows={4}
                value={adminNotes}
                onChange={e => setAdminNotes(e.target.value)}
                placeholder="توضیحات انجام کار، آدرس راه‌حل یا یادداشت نهایی برای کاربر را وارد کنید..."
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Subcomponents helper
function BoardCard({ ticket, onOpen, children, showStatus }) {
  const cat = CATEGORIES[ticket.category] || { label: ticket.category, emoji: '🦉' };
  const u = ticket.user || {};
  const displayName = u.farsiFirstName && u.farsiLastName 
    ? `${u.farsiFirstName} ${u.farsiLastName}` 
    : `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username || 'نامشخص';

  return (
    <Card 
      size="small" 
      hoverable
      onClick={() => onOpen(ticket)}
      style={{ marginBottom: 12, borderRight: `4px solid ${STATUS_COLORS[ticket.status]}` }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: '#888' }}>
          {cat.emoji} {cat.label.split(' (')[0]}
        </span>
        {ticket.aiAnswered && (
          <Tag color="purple" style={{ margin: 0 }} size="small">AI</Tag>
        )}
      </div>
      
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{ticket.title}</div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: '#666' }}>
        <span>👤 {displayName}</span>
        <span>{new Date(ticket.createdAt).toLocaleDateString('fa-IR')}</span>
      </div>

      {showStatus && (
        <div style={{ marginTop: 8 }}>
          <Tag color={STATUS_COLORS[ticket.status]}>{STATUS_LABELS[ticket.status]}</Tag>
        </div>
      )}

      {children}
    </Card>
  );
}

function EmptyColumn() {
  return (
    <div style={{ textAlign: 'center', padding: '30px 10px', color: '#999', border: '1px dashed #d9d9d9', borderRadius: 6, background: '#fff' }}>
      هیچ نامه‌ای در این ستون نیست
    </div>
  );
}
